// src/components/VoiceRecorder.jsx

import React, { useState, useRef, useEffect } from 'react';

const VoiceRecorder = ({ onVoiceRecorded, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const playbackTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (timerRef.current) clearInterval(timerRef.current);
      if (playbackTimerRef.current) clearInterval(playbackTimerRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      setPermissionDenied(false);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        setAudioBlob(audioBlob);
        
        // Convert blob to File for upload
        const audioFile = new File(
          [audioBlob], 
          `voice_note_${Date.now()}.${mediaRecorder.mimeType.includes('webm') ? 'webm' : 'mp4'}`,
          { type: mediaRecorder.mimeType }
        );
        
        onVoiceRecorded(audioFile);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      setPermissionDenied(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const playRecording = () => {
    if (!audioBlob) return;

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      return;
    }

    const audioUrl = URL.createObjectURL(audioBlob);
    audioRef.current = new Audio(audioUrl);
    
    audioRef.current.onended = () => {
      setIsPlaying(false);
      setPlaybackProgress(0);
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
    };

    audioRef.current.play();
    setIsPlaying(true);

    // Update progress
    playbackTimerRef.current = setInterval(() => {
      if (audioRef.current) {
        const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
        setPlaybackProgress(progress);
      }
    }, 100);
  };

  const deleteRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    setAudioBlob(null);
    setIsPlaying(false);
    setPlaybackProgress(0);
    onVoiceRecorded(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (permissionDenied) {
    return (
      <div style={{
        padding: 16,
        background: '#fee2e2',
        borderRadius: 8,
        border: '1px solid #fca5a5'
      }}>
        <p style={{ margin: 0, fontSize: 14, color: '#991b1b', fontWeight: 500 }}>
          Microphone access denied. Please enable microphone permissions in your browser.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      padding: 16,
      background: '#fff7ed',
      borderRadius: 8,
      border: '1px solid #fed7aa'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12
      }}>
        {/* Left side - Recording indicator and status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: isRecording ? '#ef4444' : '#f97316',
            animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none'
          }} />
          
          <div>
            {isRecording ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#ea580c' }}>
                  Recording...
                </div>
                <div style={{ fontSize: 12, color: '#9a3412' }}>
                  {formatTime(recordingTime)}
                </div>
              </>
            ) : audioBlob ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#ea580c' }}>
                  Voice Note Ready
                </div>
                <div style={{
                  width: 120,
                  height: 4,
                  background: '#fed7aa',
                  borderRadius: 2,
                  marginTop: 4,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${playbackProgress}%`,
                    height: '100%',
                    background: '#f97316',
                    transition: 'width 0.1s linear'
                  }} />
                </div>
              </>
            ) : (
              <div style={{ fontSize: 14, fontWeight: 500, color: '#9a3412' }}>
                Record a voice note
              </div>
            )}
          </div>
        </div>

        {/* Right side - Controls */}
        <div style={{ display: 'flex', gap: 8 }}>
          {!audioBlob ? (
            // Recording controls
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={disabled}
              style={{
                background: isRecording ? '#dc2626' : '#f97316',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1
              }}
            >
              {isRecording ? 'Stop' : 'Record'}
            </button>
          ) : (
            // Playback controls
            <>
              <button
                type="button"
                onClick={playRecording}
                disabled={disabled}
                style={{
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1
                }}
              >
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              <button
                type="button"
                onClick={deleteRecording}
                disabled={disabled}
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1
                }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default VoiceRecorder;

